import {
  useTable,
  Table,
  TextField,
  Button,
  Descriptions,
  Icons,
  useModalForm,
  EditButton,
  Space,
  Steps,
} from '@pankod/refine-antd'
import React from 'react'
import {
  useCan,
  useDelete,
  useGetIdentity,
  useInvalidate,
} from '@pankod/refine-core'
import dayjs, { Dayjs } from 'dayjs'
import clsx from 'clsx'

import { PopDeleteConfirm } from '@resources/lms-management/components/PopDeleteConfirm'
import { TCommonError, TLMSCommonError } from 'src/interfaces/common'
import { PROGRAM_STATE } from '../../program/types'
import { ModalForm } from './ModalForm'
import {
  PROGRAM_LOCAL_STATE,
  PROGRAM_LOCAL_STATE_LABEL,
  PROGRAM_LOCAL_STATES,
  TLMSProgramLocal,
  TLMSProgramLocalRequest,
} from '../types'
import ShowProgramLocalButton from './ShowProgramLocalButton'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { useProgramStore } from '@resources/lms-management/program/store'
import { castAsTuple } from '@resources/lms-management/types'

type ProgramLocalTableListProps = {
  programId?: number
  programName?: string
  programResourceName: string
  programStatus?: PROGRAM_STATE
  hideTitle?: boolean
}

export const ProgramLocalTableList = ({
  programId,
  programName,
  programResourceName,
  programStatus = PROGRAM_STATE.ENDED,
  hideTitle = false,
}: ProgramLocalTableListProps) => {
  const invalidate = useInvalidate()
  const { data, isSuccess } = useGetIdentity()
  const LMSRole = data?.user?.LMSRole || ''
  const PROGRAM_LOCAL_RESOURCE = `${programResourceName}/${programId}/local-programs`
  const PROGRAM_LOCAL_ENTITIES_RESOURCE = `entities/programs/${programId}/local-programs`
  const { tableProps, tableQueryResult } = useTable<
    TLMSProgramLocal,
    TCommonError
  >({
    resource:
      LMSRole === 'admin-entitas'
        ? PROGRAM_LOCAL_ENTITIES_RESOURCE
        : PROGRAM_LOCAL_RESOURCE,
    dataProviderName: 'lms',
    queryOptions: {
      enabled: !!programId && isSuccess,
    },
  })

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose,
    submit: createSubmit,
  } = useModalForm<TLMSProgramLocal, TLMSCommonError, TLMSProgramLocalRequest>({
    action: 'create',
    resource: PROGRAM_LOCAL_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    submit: ({ periode, ...formValues }) => {
      const [startDate, endDate] = castAsTuple<Dayjs>(periode)
      return {
        entityId: formValues.entityId,
        kecamatanId: formValues.kecamatanId,
        kotaKabId: formValues.kotaKabId,
        name: formValues.name,
        provinsiId: formValues.provinsiId,
        status: formValues.status,
        startDate: startDate?.utc().startOf('day').toISOString(),
        endDate: endDate?.utc().startOf('day').toISOString(),
      }
    },
    onMutationSuccess() {
      if (LMSRole === 'admin-entitas') {
        invalidate({
          resource: PROGRAM_LOCAL_ENTITIES_RESOURCE,
          dataProviderName: 'lms',
          invalidates: ['list'],
        })
      }
    },
    successNotification: {
      message: 'Program lokal berhasil dibuat',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(
        error,
        'Terdapat gangguan saat membuat program lokal',
      ),
  })

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editShow,
    close: editClose,
    submit: editSubmit,
  } = useModalForm<TLMSProgramLocal, TLMSCommonError, TLMSProgramLocalRequest>({
    action: 'edit',
    resource: PROGRAM_LOCAL_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    submit: ({ periode, ...formValues }) => {
      const [startDate, endDate] = castAsTuple<Dayjs | undefined>(periode)
      return {
        entityId: formValues.entityId,
        kecamatanId: formValues.kecamatanId,
        kotaKabId: formValues.kotaKabId,
        name: formValues.name,
        provinsiId: formValues.provinsiId,
        status: formValues.status,
        startDate: startDate?.utc().startOf('day').toISOString(),
        endDate: endDate?.utc().startOf('day').toISOString(),
      }
    },
    onMutationSuccess() {
      if (LMSRole === 'admin-entitas') {
        invalidate({
          resource: PROGRAM_LOCAL_ENTITIES_RESOURCE,
          dataProviderName: 'lms',
          invalidates: ['list'],
        })
      }
    },
    successNotification: {
      message: 'Program lokal berhasil diubah',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(
        error,
        'Terdapat gangguan saat mengubah program lokal',
      ),
  })

  const { mutateAsync } = useDelete()

  const { data: canDelete } = useCan({
    action: 'delete',
    resource: 'lms-program-local-management',
  })

  const { data: canEdit } = useCan({
    action: 'edit',
    resource: 'lms-program-local-management',
  })

  const { data: canCreate } = useCan({
    action: 'create',
    resource: 'lms-program-local-management',
  })

  const isOwner = useProgramStore(({ isOwner }) => isOwner)

  const allowDeleteActionByRole = canDelete?.can || isOwner
  const allowEditActionByRole = canEdit?.can || isOwner

  const disableCreateAction =
    programStatus !== PROGRAM_STATE.PREPARATION || (!canCreate?.can && !isOwner)

  return (
    <>
      <Descriptions
        title={hideTitle ? null : 'Program Lokal'}
        extra={
          <Button
            disabled={disableCreateAction}
            onClick={() => createShow()}
            icon={<Icons.PlusOutlined />}
          >
            Tambah Program Lokal
          </Button>
        }
      >
        <Descriptions.Item span={1} contentStyle={{ width: '100%' }}>
          <Table
            {...tableProps}
            rowKey="id"
            className="w-full"
            bordered
            size="small"
            scroll={{ x: 800 }}
            loading={tableQueryResult.isFetching}
            expandable={{
              expandedRowRender(record) {
                return (
                  <Descriptions title="Wilayah Program Lokal" colon column={3}>
                    <Descriptions.Item label="Provinsi">
                      {record.provinsi ? (
                        record.provinsi
                      ) : (
                        <TextField value="Kosong" disabled italic />
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Kota/Kabupaten">
                      {record.kotaKab ? (
                        record.kotaKab
                      ) : (
                        <TextField value="Kosong" disabled italic />
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Kecamatan">
                      {record.kecamatan ? (
                        record.kecamatan
                      ) : (
                        <TextField value="Kosong" disabled italic />
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                )
              },
            }}
          >
            <Table.Column
              dataIndex="id"
              title="ID"
              width={80}
              render={(value) => <TextField value={value} />}
            />
            <Table.Column
              dataIndex="name"
              width={300}
              title="Nama Program Lokal"
              render={(value) => <TextField value={value} />}
            />
            <Table.Column
              dataIndex={['entity', 'name']}
              title="Entitas"
              width={200}
              render={(value) => <TextField value={value} />}
            />
            <Table.Column<TLMSProgramLocal>
              title="Periode"
              width={130}
              render={(_, record) =>
                record.startDate && record.endDate ? (
                  <>
                    {dayjs(record.startDate).format('DD MMM YYYY')} &mdash;{' '}
                    {dayjs(record.endDate).format('DD MMM YYYY')}
                  </>
                ) : (
                  <TextField value="Kosong" disabled italic />
                )
              }
            />
            <Table.Column
              dataIndex="status"
              width={130}
              title="Status"
              render={(
                value: PROGRAM_LOCAL_STATE = PROGRAM_LOCAL_STATE.DRAFT,
              ) => {
                return (
                  <Steps
                    direction="vertical"
                    size="small"
                    current={PROGRAM_LOCAL_STATES.findIndex((v) => v === value)}
                    progressDot={(dot, { status, index }) =>
                      status === 'process' ? (
                        <span className={clsx(index === 1 && 'animate-ping')}>
                          {dot}
                        </span>
                      ) : (
                        dot
                      )
                    }
                  >
                    {PROGRAM_LOCAL_STATES.map((state) => (
                      <Steps.Step
                        key={state}
                        title={PROGRAM_LOCAL_STATE_LABEL[state]}
                        className="capitalize"
                      />
                    ))}
                  </Steps>
                )
              }}
            />
            <Table.Column<TLMSProgramLocal>
              title="Aksi"
              fixed="right"
              width={100}
              render={(_, record) => (
                <Space direction="vertical">
                  <ShowProgramLocalButton
                    block
                    size="small"
                    recordItemId={record.id}
                    programId={programId}
                  >
                    Lihat
                  </ShowProgramLocalButton>
                  <EditButton
                    size="small"
                    block
                    recordItemId={record.id}
                    ignoreAccessControlProvider
                    disabled={
                      !allowEditActionByRole ||
                      record.status === PROGRAM_LOCAL_STATE.DONE
                    }
                    onClick={() => {
                      editShow(record.id)
                    }}
                  >
                    Ubah
                  </EditButton>
                  <PopDeleteConfirm
                    title={`Apakah Anda yakin ingin menghapus Program Lokal "${record.name}"?`}
                    placeholder="Tulis ulang nama Program Lokal"
                    onConfirm={() =>
                      mutateAsync(
                        {
                          id: record.id,
                          resource: PROGRAM_LOCAL_RESOURCE,
                          dataProviderName: 'lms',
                          successNotification: {
                            message: 'Program lokal berhasil dihapus',
                            type: 'success',
                            description: 'Sukses',
                          },
                          errorNotification: (error) =>
                            showErrorNotification(
                              error,
                              'Terdapat gangguan saat menghapus program lokal',
                            ),
                        },
                        {
                          onSuccess() {
                            if (LMSRole === 'admin-entitas') {
                              invalidate({
                                resource: PROGRAM_LOCAL_ENTITIES_RESOURCE,
                                dataProviderName: 'lms',
                                invalidates: ['list'],
                              })
                            }
                          },
                        },
                      )
                    }
                    okButtonProps={{
                      danger: true,
                    }}
                    okText="Hapus"
                    cancelText="Batal"
                    validateValue={record.name}
                    errorMessage="Nama Program Lokal tidak sama"
                    disabled={
                      !allowDeleteActionByRole ||
                      record.status !== PROGRAM_LOCAL_STATE.DRAFT
                    }
                  >
                    <Button
                      size="small"
                      danger
                      block
                      disabled={
                        !allowDeleteActionByRole ||
                        record.status !== PROGRAM_LOCAL_STATE.DRAFT
                      }
                      icon={<Icons.DeleteOutlined />}
                    >
                      Hapus
                    </Button>
                  </PopDeleteConfirm>
                </Space>
              )}
            />
          </Table>
        </Descriptions.Item>
      </Descriptions>

      <ModalForm
        formProps={createFormProps}
        modalProps={createModalProps}
        mode="create"
        programName={programName}
        onClose={() => {
          createFormProps.form?.resetFields()
          createClose()
        }}
        onSubmit={createSubmit}
      />

      <ModalForm
        formProps={editFormProps}
        modalProps={editModalProps}
        mode="edit"
        programName={programName}
        onClose={() => {
          editFormProps.form?.resetFields()
          editClose()
        }}
        onSubmit={editSubmit}
      />
    </>
  )
}
